import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { AdminApi } from "../api/client.js";
import type { DriftResult } from "../api/types.js";
import { Card } from "../components/Card.js";
import { Field } from "../components/Field.js";
import { Badge } from "../components/Badge.js";
import { ErrorBanner } from "../components/ErrorBanner.js";
import { formatDateTime } from "../lib/format.js";

type Props = { api: AdminApi };

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function CalendarTroubleshootPage({ api }: Props) {
  const today = useMemo(() => new Date(), []);
  const [fromDate, setFromDate] = useState(isoDate(today));
  const [toDate, setToDate] = useState(
    isoDate(new Date(today.getTime() + 14 * 86400_000)),
  );
  const [durationMinutes, setDurationMinutes] = useState(30);

  const snapshot = useQuery({
    queryKey: ["admin", "troubleshoot", "snapshot", fromDate, toDate, durationMinutes],
    queryFn: () => api.getTroubleshootSnapshot({ fromDate, toDate, durationMinutes }),
  });

  const drift = useMutation<DriftResult, Error>({
    mutationFn: () => api.runTroubleshootDrift({ fromDate, toDate }),
  });

  return (
    <div className="grid gap-4">
      <Card title="Sync health">
        {snapshot.isLoading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : snapshot.isError ? (
          <ErrorBanner message={(snapshot.error as Error).message} />
        ) : snapshot.data && snapshot.data.calendars.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500">
                <th className="py-1 pr-4">Calendar</th>
                <th className="py-1 pr-4">Backfill</th>
                <th className="py-1 pr-4">Channel expires</th>
                <th className="py-1 pr-4">Last synced</th>
                <th className="py-1 pr-4">Last error</th>
              </tr>
            </thead>
            <tbody>
              {snapshot.data.calendars.map((c) => (
                <tr key={c.calendar_id} className="border-t border-slate-100">
                  <td className="py-1 pr-4 font-mono text-xs">{c.calendar_id}</td>
                  <td className="py-1 pr-4">
                    {c.initial_backfill_done ? (
                      <Badge tone="emerald">done</Badge>
                    ) : (
                      <Badge tone="amber">syncing</Badge>
                    )}
                  </td>
                  <td className="py-1 pr-4">
                    {c.channel_expiration ? formatDateTime(c.channel_expiration) : "—"}
                  </td>
                  <td className="py-1 pr-4">
                    {c.last_synced_at ? formatDateTime(c.last_synced_at) : "never"}
                  </td>
                  <td className="py-1 pr-4">
                    {c.last_sync_error ? (
                      <Badge tone="rose">{c.last_sync_error}</Badge>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-sm text-slate-500">No busy calendars selected.</p>
        )}
      </Card>

      <Card
        title="Drift vs Google"
        action={
          <button
            type="button"
            className="rounded bg-blue-600 px-3 py-1 text-sm font-medium text-white disabled:opacity-50"
            disabled={drift.isPending}
            onClick={() => drift.mutate()}
          >
            {drift.isPending ? "Checking…" : "Run live check"}
          </button>
        }
      >
        {drift.isError ? (
          <ErrorBanner message={(drift.error as Error).message} />
        ) : drift.data ? (
          <div className="grid gap-3 text-sm">
            <p>
              <Badge tone={drift.data.missing_from_mirror.length ? "rose" : "emerald"}>
                {drift.data.missing_from_mirror.length} missing from mirror
              </Badge>{" "}
              <Badge tone={drift.data.extra_in_mirror.length ? "amber" : "emerald"}>
                {drift.data.extra_in_mirror.length} extra in mirror
              </Badge>
            </p>
            <DriftList title="Missing from mirror (stale/missed)" rows={drift.data.missing_from_mirror} />
            <DriftList title="Extra in mirror (missed deletion)" rows={drift.data.extra_in_mirror} />
          </div>
        ) : (
          <p className="text-sm text-slate-500">
            Compares Google’s live free/busy against our mirror for the range below. No event titles are read.
          </p>
        )}
        <p className="mt-2 text-xs text-slate-400">
          Note: small or day-boundary differences can reflect all-day / tentative
          event handling rather than a real sync miss. Treat whole missing blocks
          as the signal.
        </p>
      </Card>

      <Card title="Range explorer">
        <div className="flex flex-wrap items-end gap-3">
          <Field
            label="From"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
          <Field
            label="To"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
          <Field
            label="Duration (min)"
            type="number"
            value={durationMinutes}
            onChange={(e) => setDurationMinutes(Number(e.target.value))}
          />
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <h3 className="mb-2 text-sm font-semibold text-slate-700">Busy blocks</h3>
            {snapshot.data && snapshot.data.busy_blocks.length > 0 ? (
              <ul className="space-y-1 text-sm">
                {snapshot.data.busy_blocks.map((b, i) => (
                  <li key={`${b.calendar_id}-${i}`} className="font-mono text-xs">
                    {formatDateTime(b.start_at)} → {formatDateTime(b.end_at)} ({b.status})
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-slate-500">No busy blocks in range.</p>
            )}
          </div>
          <div>
            <h3 className="mb-2 text-sm font-semibold text-slate-700">Computed availability</h3>
            {snapshot.data && snapshot.data.slots.length > 0 ? (
              <ul className="space-y-1 text-sm">
                {snapshot.data.slots.map((s, i) => (
                  <li key={i} className="font-mono text-xs">
                    {formatDateTime(s.startsAt)} → {formatDateTime(s.endsAt)}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-slate-500">No open slots in range.</p>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

function DriftList({ title, rows }: { title: string; rows: { start: string; end: string }[] }) {
  if (rows.length === 0) return null;
  return (
    <div>
      <h3 className="mb-1 text-sm font-semibold text-slate-700">{title}</h3>
      <ul className="space-y-1">
        {rows.map((r, i) => (
          <li key={i} className="font-mono text-xs">
            {formatDateTime(r.start)} → {formatDateTime(r.end)}
          </li>
        ))}
      </ul>
    </div>
  );
}
